/**
 * WeaponBox - Destructible crate that upgrades weapons
 *
 * Features:
 * - Square with notched corners
 * - HP bar displayed above
 * - Takes damage from player bullets
 * - When destroyed: triggers weapon upgrade
 * - Weapon sequence: Pistol → Rifle → Machine Gun → Rocket
 */

import Phaser from 'phaser';
import {
  COLORS,
  ENTITY_PIXELS,
  GAME_HEIGHT,
} from '../config';
import { WEAPON_BOX, WeaponType } from '../utils/constants';
import { fitImageContain, getWeaponCardTextureKey, getWeaponGunTextureKey } from '../ui/weaponVisuals';

export interface WeaponBoxConfig {
  x: number;
  y: number;
  hp?: number;
  /** The weapon type this box will grant (next in sequence) */
  nextWeapon?: WeaponType;
}

export class WeaponBox extends Phaser.GameObjects.Container {
  /** Box size */
  static readonly SIZE = ENTITY_PIXELS.weaponBox; // ~56px (15% of 375)

  /** Current HP */
  private hp: number;

  /** Maximum HP */
  private maxHp: number;

  /** Whether the box is still active */
  private isActive: boolean = true;

  /** The weapon type this box grants */
  private nextWeapon: WeaponType;

  /** Box graphics */
  private box: Phaser.GameObjects.Graphics;

  /** HP bar background */
  private hpBarBg: Phaser.GameObjects.Graphics;

  /** HP bar fill */
  private hpBarFill: Phaser.GameObjects.Graphics;

  /** Subtle background card texture */
  private weaponCard: Phaser.GameObjects.Image;

  /** Weapon icon art */
  private weaponGun: Phaser.GameObjects.Image;

  /** Reference to scroll speed */
  private scrollSpeed: number = 100;

  /** HP bar dimensions */
  private static readonly HP_BAR_WIDTH = 50;
  private static readonly HP_BAR_HEIGHT = 6;
  private static readonly HP_BAR_OFFSET_Y = -40;

  constructor(scene: Phaser.Scene, config: WeaponBoxConfig) {
    super(scene, config.x, config.y);

    this.hp = config.hp ?? WEAPON_BOX.defaultHp;
    this.maxHp = this.hp;
    this.nextWeapon = config.nextWeapon ?? WeaponType.Rifle;

    // Create HP bar (behind box)
    this.hpBarBg = scene.add.graphics();
    this.hpBarFill = scene.add.graphics();
    this.add(this.hpBarBg);
    this.add(this.hpBarFill);

    // Create box graphics (subtle frame)
    this.box = scene.add.graphics();
    this.add(this.box);

    // Create weapon visuals
    this.weaponCard = scene.add.image(0, 0, getWeaponCardTextureKey(this.nextWeapon)).setOrigin(0.5);
    this.weaponCard.setAlpha(0.22);
    fitImageContain(this.weaponCard, WeaponBox.SIZE * 0.95, WeaponBox.SIZE * 0.95);
    this.add(this.weaponCard);

    this.weaponGun = scene.add.image(0, 0, getWeaponGunTextureKey(this.nextWeapon)).setOrigin(0.5);
    fitImageContain(this.weaponGun, WeaponBox.SIZE * 0.92, WeaponBox.SIZE * 0.52);
    this.add(this.weaponGun);

    // Draw everything
    this.drawBox();
    this.drawHpBar();

    scene.add.existing(this);
  }

  /**
   * Draws the box with notched corners
   */
  private drawBox(): void {
    const size = WeaponBox.SIZE;
    const halfSize = size / 2;
    const notch = 7; // Corner notch size

    this.box.clear();

    // Subtle panel fill + muted border
    this.box.fillStyle(0x1d2128, 0.16);
    this.box.beginPath();

    // Draw octagon-ish shape (square with cut corners)
    this.box.moveTo(-halfSize + notch, -halfSize);
    this.box.lineTo(halfSize - notch, -halfSize);
    this.box.lineTo(halfSize, -halfSize + notch);
    this.box.lineTo(halfSize, halfSize - notch);
    this.box.lineTo(halfSize - notch, halfSize);
    this.box.lineTo(-halfSize + notch, halfSize);
    this.box.lineTo(-halfSize, halfSize - notch);
    this.box.lineTo(-halfSize, -halfSize + notch);
    this.box.closePath();
    this.box.fillPath();

    // Thin outer border
    this.box.lineStyle(2, 0x8d835a, 0.72);
    this.box.strokePath();

    // Inner frame line
    this.box.lineStyle(1, 0xffffff, 0.16);
    this.box.beginPath();
    this.box.moveTo(-halfSize + notch + 3, -halfSize + 3);
    this.box.lineTo(halfSize - notch - 3, -halfSize + 3);
    this.box.lineTo(halfSize - 3, -halfSize + notch + 3);
    this.box.lineTo(halfSize - 3, halfSize - notch - 3);
    this.box.lineTo(halfSize - notch - 3, halfSize - 3);
    this.box.lineTo(-halfSize + notch + 3, halfSize - 3);
    this.box.lineTo(-halfSize + 3, halfSize - notch - 3);
    this.box.lineTo(-halfSize + 3, -halfSize + notch + 3);
    this.box.closePath();
    this.box.strokePath();
  }

  /**
   * Draws the HP bar
   */
  private drawHpBar(): void {
    const barWidth = WeaponBox.HP_BAR_WIDTH;
    const barHeight = WeaponBox.HP_BAR_HEIGHT;
    const offsetY = WeaponBox.HP_BAR_OFFSET_Y;
    const cornerRadius = 3;

    // Background
    this.hpBarBg.clear();
    this.hpBarBg.fillStyle(0x000000, 0.6);
    this.hpBarBg.fillRoundedRect(
      -barWidth / 2,
      offsetY - barHeight / 2,
      barWidth,
      barHeight,
      cornerRadius
    );

    // Fill based on HP
    this.updateHpBar();
  }

  /**
   * Updates the HP bar fill
   */
  private updateHpBar(): void {
    const barWidth = WeaponBox.HP_BAR_WIDTH;
    const barHeight = WeaponBox.HP_BAR_HEIGHT;
    const offsetY = WeaponBox.HP_BAR_OFFSET_Y;
    const cornerRadius = 3;

    const hpPercent = Math.max(0, this.hp / this.maxHp);
    const fillWidth = barWidth * hpPercent;

    this.hpBarFill.clear();
    this.hpBarFill.fillStyle(COLORS.gold, 1);
    this.hpBarFill.fillRoundedRect(
      -barWidth / 2,
      offsetY - barHeight / 2,
      fillWidth,
      barHeight,
      cornerRadius
    );
  }

  /**
   * Sets the scroll speed
   */
  setScrollSpeed(speed: number): void {
    this.scrollSpeed = speed;
  }

  /**
   * Updates position each frame
   */
  update(_time: number, delta: number): void {
    if (!this.isActive) return;

    // Move down at scroll speed
    const movement = (this.scrollSpeed * delta) / 1000;
    this.y += movement;

    // Check if off-screen
    if (this.y > GAME_HEIGHT + WeaponBox.SIZE) {
      this.destroy();
    }
  }

  /**
   * Takes damage from bullets
   * @returns true if the box was destroyed
   */
  takeDamage(damage: number): boolean {
    if (!this.isActive) return false;

    this.hp -= damage;
    this.updateHpBar();

    // Flash effect on hit
    this.flashOnHit();

    if (this.hp <= 0) {
      this.onDestroyed();
      return true;
    }

    return false;
  }

  /**
   * Flash effect when hit
   */
  private flashOnHit(): void {
    this.setAlpha(0.82);
    this.weaponGun.setTintFill(0xffffff);
    this.scene.time.delayedCall(60, () => {
      if (this.isActive) {
        this.setAlpha(1);
        this.weaponGun.clearTint();
      }
    });
  }

  /**
   * Just draws the box shape (for flash effect)
   */
  private drawBoxShape(): void {
    const size = WeaponBox.SIZE;
    const halfSize = size / 2;
    const notch = 8;

    this.box.beginPath();
    this.box.moveTo(-halfSize + notch, -halfSize);
    this.box.lineTo(halfSize - notch, -halfSize);
    this.box.lineTo(halfSize, -halfSize + notch);
    this.box.lineTo(halfSize, halfSize - notch);
    this.box.lineTo(halfSize - notch, halfSize);
    this.box.lineTo(-halfSize + notch, halfSize);
    this.box.lineTo(-halfSize, halfSize - notch);
    this.box.lineTo(-halfSize, -halfSize + notch);
    this.box.closePath();
    this.box.fillPath();
  }

  /**
   * Called when box HP reaches 0
   */
  private onDestroyed(): void {
    this.isActive = false;

    // Emit event with the weapon type
    this.emit('destroyed', this.nextWeapon);

    // Play destruction animation
    this.playDestroyAnimation();
  }

  /**
   * Plays the destruction/upgrade animation
   */
  private playDestroyAnimation(): void {
    // Hide HP bar
    this.hpBarBg.setVisible(false);
    this.hpBarFill.setVisible(false);

    // Bright final flash
    this.box.clear();
    this.box.fillStyle(0xffffff, 0.8);
    this.drawBoxShape();
    this.weaponGun.setTintFill(0xffffff);
    this.weaponCard.setAlpha(0.35);

    // Scale up and fade
    this.scene.tweens.add({
      targets: this,
      scaleX: 1.5,
      scaleY: 1.5,
      alpha: 0,
      duration: 200,
      ease: 'Power2',
      onComplete: () => {
        this.createDestroyParticles();
        this.destroy();
      },
    });
  }

  /**
   * Creates particle burst on destruction
   */
  private createDestroyParticles(): void {
    const particleCount = Phaser.Math.Between(8, 12);

    for (let i = 0; i < particleCount; i++) {
      const particle = this.scene.add.graphics();
      particle.fillStyle(COLORS.gold, 0.8);

      // Small square particles
      const size = Phaser.Math.Between(4, 10);
      particle.fillRect(-size / 2, -size / 2, size, size);

      particle.setPosition(this.x, this.y);

      // Random direction
      const angle = (i / particleCount) * Math.PI * 2;
      const velocity = Phaser.Math.Between(80, 150);
      const vx = Math.cos(angle) * velocity;
      const vy = Math.sin(angle) * velocity;

      this.scene.tweens.add({
        targets: particle,
        x: particle.x + vx * 0.4,
        y: particle.y + vy * 0.4,
        alpha: 0,
        rotation: Phaser.Math.FloatBetween(-Math.PI, Math.PI),
        duration: 350,
        ease: 'Power2',
        onComplete: () => particle.destroy(),
      });
    }
  }

  /**
   * Gets the weapon type this box grants
   */
  getNextWeapon(): WeaponType {
    return this.nextWeapon;
  }

  /**
   * Gets current HP
   */
  getHp(): number {
    return this.hp;
  }

  /**
   * Gets max HP
   */
  getMaxHp(): number {
    return this.maxHp;
  }

  /**
   * Returns whether box is still active
   */
  getIsActive(): boolean {
    return this.isActive;
  }

  /**
   * Gets collision radius for bullet hits
   */
  getCollisionRadius(): number {
    return WeaponBox.SIZE / 2;
  }

  /**
   * Cleanup
   */
  destroy(fromScene?: boolean): void {
    this.isActive = false;
    super.destroy(fromScene);
  }
}
